import React, { useEffect, useState } from "react";
import axios from "axios";
import { useParams, Link } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";
import { parsePhoneNumberFromString } from "libphonenumber-js";

const OrdersView = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, defaultValue) => translations[key] || defaultValue;
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [cdnConfig, setCdnConfig] = useState(null);
  const [trackingNumber, setTrackingNumber] = useState("");
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [preparedItems, setPreparedItems] = useState({});
  const [paymentNoteModal, setPaymentNoteModal] = useState(false);
  const [paymentNote, setPaymentNote] = useState("");
  const [pendingStatus, setPendingStatus] = useState(null);
  const togglePrepared = (itemId) => {
    setPreparedItems((prev) => ({
      ...prev,
      [itemId]: !prev[itemId],
    }));
  };
  const areAllPrepared = (items) => {
    if (!items || items.length === 0) return false;
    return items.every((item) => preparedItems[item.id]);
  };

  useEffect(() => {
    setTrackingNumber(order?.tracking_number || "");
  }, [order]);

  const API = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    fetchCdnConfig();
  }, []);

  const fetchCdnConfig = async () => {
    try {
      const res = await axios.get(`${API}/api/cdn-config`);
      setCdnConfig(res.data);
    } catch (err) {
      console.error("CDN config alınamadı:", err);
    }
  };

  const buildImageUrl = (cdnImage, mainImage) => {
    const filename = cdnImage || mainImage;
    if (!filename) return null;
    if (filename.startsWith("http")) return filename;
    if (filename.startsWith("/cdn/")) {
      return `${API}${filename}`;
    }
    if (cdnConfig?.cdn_path && cdnConfig?.cdn_folder) {
      return `${cdnConfig.cdn_path}/${cdnConfig.cdn_folder}/${filename}`;
    }
    return `${API}/cdn/${filename}`;
  };

  // ─── Dosya önizleme helper ────────────────────────────────────────────────────
  const renderFiles = (files) => {
    if (!files || files.length === 0) return null;

    return (
      <div style={{ marginTop: "10px" }}>
        <strong style={{ fontSize: "12px", color: "#6b7280" }}>
          Ekli Dosyalar:
        </strong>
        <div
          style={{
            display: "flex",
            gap: "8px",
            flexWrap: "wrap",
            marginTop: "6px",
          }}
        >
          {files.map((filename, idx) => {
            const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(filename);
            const isPdf = /\.pdf$/i.test(filename);
            const fileUrl = `${API}/cdn/${filename}`;

            if (isImage) {
              return (
                <a
                  key={idx}
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={filename}
                  style={{
                    display: "block",
                    width: "64px",
                    height: "64px",
                    borderRadius: "6px",
                    overflow: "hidden",
                    border: "1px solid #e5e7eb",
                    flexShrink: 0,
                  }}
                >
                  <img
                    src={fileUrl}
                    alt={filename}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.style.display = "none";
                    }}
                  />
                </a>
              );
            }

            if (isPdf) {
              return (
                <a
                  key={idx}
                  href={fileUrl}
                  target="_blank"
                  rel="noreferrer"
                  title={filename}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "64px",
                    height: "64px",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                    background: "#fef2f2",
                    textDecoration: "none",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className="ph ph-file-pdf"
                    style={{ fontSize: "28px", color: "#ef4444" }}
                  />
                  <span
                    style={{
                      fontSize: "9px",
                      color: "#6b7280",
                      marginTop: "2px",
                      textAlign: "center",
                      wordBreak: "break-all",
                      padding: "0 2px",
                      lineHeight: 1.2,
                    }}
                  >
                    {filename.length > 10
                      ? filename.substring(0, 8) + "…"
                      : filename}
                  </span>
                </a>
              );
            }

            // Diğer dosya tipleri
            return (
              <a
                key={idx}
                href={fileUrl}
                target="_blank"
                rel="noreferrer"
                title={filename}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "64px",
                  height: "64px",
                  borderRadius: "6px",
                  border: "1px solid #e5e7eb",
                  background: "#f9fafb",
                  textDecoration: "none",
                  flexShrink: 0,
                }}
              >
                <i
                  className="ph ph-file"
                  style={{ fontSize: "28px", color: "#6b7280" }}
                />
                <span
                  style={{
                    fontSize: "9px",
                    color: "#6b7280",
                    marginTop: "2px",
                    textAlign: "center",
                    wordBreak: "break-all",
                    padding: "0 2px",
                    lineHeight: 1.2,
                  }}
                >
                  {filename.length > 10
                    ? filename.substring(0, 8) + "…"
                    : filename}
                </span>
              </a>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    axios
      .get(`${API}/api/orders/details/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        },
      })
      .then((res) => setOrder(res.data))
      .catch(() => {});
  }, [id]);

  if (!order) return <p>{getText("LOADING", "Yükleniyor...")}</p>;
  let shippingData = null;

  try {
    shippingData =
      typeof order.shipping_address_json === "string"
        ? JSON.parse(order.shipping_address_json)
        : order.shipping_address_json;
  } catch {
    shippingData = null;
  }

  const STATUS_OPTIONS = [
    {
      value: "pending",
      label: getText("ORDER_STEP_PENDING", "Ödeme Bekliyor"),
    },

    {
      value: "paid",
      label: getText("ORDER_STATUS_PENDING", "Sipariş Alındı"),
    },

    {
      value: "shipped",
      label: getText("ORDER_STATUS_SHIPPED", "Kargoya Verildi"),
    },

    {
      value: "delivered",
      label: getText("ORDER_STATUS_DELIVERED", "Teslim Edildi"),
    },

    {
      value: "cancelled",
      label: getText("ORDER_STATUS_CANCELLED", "İptal Edildi"),
    },
    {
      value: "partially_cancelled",
      label: getText("ORDER_STATUS_PARTIALLY_CANCELLED", "Kısmi İptal"),
    },
    {
      value: "partially_returned",
      label: getText("ORDER_STATUS_PARTIALLY_RETURNED", "Kısmi İade"),
    },
    {
      value: "fully_returned",
      label: getText("ORDER_STATUS_FULLY_RETURNED", "Tam İade"),
    },
  ];

  const isTrackingEditable = order.status === "paid";

  // ─── ANA SİPARİŞ DURUMU GÜNCELLEME ─────────────────────────────────────────
  const handleStatusChange = async (newStatus) => {
    if (newStatus === "shipped" && !trackingNumber) {
      alert("Kargo takip numarası girilmeden kargoya verilemez!");
      return;
    }

    // pending → paid geçişinde not modalını aç
    if (order.status === "pending" && newStatus === "paid") {
      setPendingStatus(newStatus);
      setPaymentNoteModal(true);
      return;
    }

    await doStatusUpdate(newStatus, null);
  };

  const doStatusUpdate = async (newStatus, note) => {
    try {
      setStatusUpdating(true);
      await axios.put(
        `${API}/api/orders/orders/${order.id}/status`,
        { status: newStatus, note: note || undefined },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        },
      );
      setOrder((prev) => ({
        ...prev,
        status: newStatus,
        note: note || prev.note,
      }));
    } catch (err) {
      console.error("Status update error:", err);
      alert("Durum güncellenemedi");
    } finally {
      setStatusUpdating(false);
    }
  };

  const handlePaymentNoteConfirm = async () => {
    setPaymentNoteModal(false);
    await doStatusUpdate(pendingStatus, paymentNote);
    setPaymentNote("");
    setPendingStatus(null);
  };

  // ─── İADE DURUMU GÜNCELLEME (BİREYSEL) ─────────────────────────────────────
  const handleReturnStatusChange = async (itemId, returnId, newStatus) => {
    try {
      setStatusUpdating(true);
      await axios.put(
        `${API}/api/orders/returns/${returnId}/status`,
        { status: newStatus },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        },
      );

      // Local state'i anlık güncelle
      setOrder((prev) => {
        const updatedItems = prev.items.map((item) => {
          if (item.id === itemId && item.returns) {
            const updatedReturns = item.returns.map((ret) =>
              ret.id === returnId ? { ...ret, status: newStatus } : ret,
            );
            return { ...item, returns: updatedReturns };
          }
          return item;
        });
        return { ...prev, items: updatedItems };
      });
    } catch (err) {
      console.error("Return status update error:", err);
      alert("İade durumu güncellenemedi");
    } finally {
      setStatusUpdating(false);
    }
  };

  const formatDateTR = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleString("tr-TR", {
      timeZone: "Europe/Istanbul",
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatPhone = (phone) => {
    if (!phone) return "-";
    try {
      const raw = "+" + phone.toString().replace(/\D/g, "");
      const parsed = parsePhoneNumberFromString(raw);
      if (parsed && parsed.isPossible()) {
        return parsed.formatInternational();
      }
      return raw;
    } catch (e) {
      return phone;
    }
  };

  const handleTrackingSave = async () => {
    if (!trackingNumber) return;

    try {
      setTrackingLoading(true);

      await axios.put(
        `${API}/api/orders/tracking/${order.id}`,
        { tracking_number: trackingNumber },
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
          },
        },
      );

      // 🔥 UI güncelle
      setOrder((prev) => ({
        ...prev,
        tracking_number: trackingNumber,
        status: "shipped",
      }));
    } catch (err) {
      console.error(err);
      alert("Kargo bilgisi kaydedilemedi");
    } finally {
      setTrackingLoading(false);
    }
  };

  // ─── İade kontrolü ve Ana Durum Kilidi ───
  const hasAnyReturns = order.items?.some(
    (item) => item.returns && item.returns.length > 0,
  );
  const currentMainStatus = hasAnyReturns ? "delivered" : order.status;

  // KİLİT MEKANİZMASI: İptal edildiğinde, Teslim edildiğinde veya İade olduğunda kilitlenir.
  const isMainStatusDisabled =
    statusUpdating ||
    order.status === "cancelled" ||
    order.status === "delivered" ||
    hasAnyReturns;

  // ─── Ürün tabloları ───────────────────────────────────────────────────────
  const renderProductTables = () => {
    const allItems = order.items || [];
    const isFullyCancelled = order.status === "cancelled";

    const activeItems = [];
    const cancelledItems = [];
    const returnRows = [];

    allItems.forEach((item) => {
      const cancelledQty = item.cancelled_qty || 0;

      // İadeleri ayıkla ve topla
      let totalReturnedForThisItem = 0;
      if (item.returns && item.returns.length > 0) {
        item.returns.forEach((ret) => {
          totalReturnedForThisItem += ret.returned_qty || 0;
          returnRows.push({ itemRef: item, retData: ret });
        });
      }

      // Aktif miktarı hesapla (Toplam - İptal - İade)
      const activeQty =
        item.active_qty ?? item.qty - cancelledQty - totalReturnedForThisItem;

      if (cancelledQty > 0) {
        cancelledItems.push({
          ...item,
          qty: cancelledQty,
          total: item.price * cancelledQty,
        });
      }

      if (activeQty > 0 && !item.is_cancelled) {
        activeItems.push({
          ...item,
          qty: activeQty,
          total: item.price * activeQty,
        });
      } else if (item.is_cancelled && cancelledQty === 0) {
        cancelledItems.push({ ...item, qty: item.qty });
      }
    });

    const isPartialCancel = cancelledItems.length > 0 && !isFullyCancelled;
    const activeTotal = activeItems.reduce(
      (sum, item) => sum + item.qty * item.price,
      0,
    );

    const renderRow = (item, isCancelled, index) => (
      <React.Fragment key={`${item.id}-${isCancelled ? "c" : "a"}-${index}`}>
        <tr className={isCancelled ? "table-danger" : ""}>
          <td>{index + 1}</td>
          <td>
            {item.main_image ? (
              <img
                src={buildImageUrl(item.main_image_cdn, item.main_image)}
                alt={item.title}
                style={{
                  width: "55px",
                  height: "55px",
                  objectFit: "cover",
                  borderRadius: "8px",
                  border: "1px solid #eee",
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "none";
                }}
              />
            ) : (
              <span className="text-muted"> </span>
            )}
          </td>
          <td>
            <div className="fw-bold">{item.barcode || "—"}</div>
            {item.sku && (
              <small className="text-muted d-block">
                {getText("SKU_LABEL", "SKU")}: {item.sku}
              </small>
            )}
          </td>
          <td>
            <div
              className="fw-bold"
              style={isCancelled ? { color: "#ef4444" } : {}}
            >
              {item.title}
            </div>
            {isCancelled && (
              <span
                className="badge mt-1 d-inline-flex align-items-center gap-1"
                style={{
                  background: "#fee2e2",
                  color: "#dc2626",
                  fontSize: "10px",
                  padding: "2px 7px",
                  borderRadius: "99px",
                  fontWeight: 600,
                }}
              >
                ✕ {getText("ORDER_STATUS_CANCELLED", "İptal Edildi")}
              </span>
            )}
          </td>
          <td>
            <span
              className="badge px-2 py-1"
              style={
                isCancelled
                  ? { background: "#fee2e2", color: "#dc2626" }
                  : { background: "#f1f5f9", color: "#0f172a" }
              }
            >
              {item.qty}
            </span>
          </td>
          <td>
            {item.price} {item.currency_symbol}
          </td>
          <td className="fw-bold">
            {(item.qty * item.price).toFixed(2)} {item.currency_symbol}
            {isCancelled && item.refund_amount > 0 && (
              <div className="small text-danger">
                İade: {item.refund_amount} {item.currency_symbol}
              </div>
            )}
          </td>
          <td>
            {isCancelled ? (
              <span className="badge bg-danger">✕ İptal</span>
            ) : (
              <span className="badge bg-success">Aktif</span>
            )}
          </td>
        </tr>

        {/* 🔥 İPTAL SEBEBİ VE NOTU DETAY SATIRI 🔥 */}
        {isCancelled && item.cancellations && item.cancellations.length > 0 && (
          <tr className="table-danger border-top-0">
            <td colSpan="8" className="py-2">
              <div style={{ paddingLeft: "15px" }}>
                {item.cancellations.map((c, idx) => (
                  <div
                    key={idx}
                    className="mb-2 p-2 small"
                    style={{
                      background: "#fff5f5",
                      borderRadius: "4px",
                      border: "1px solid #fca5a5",
                      borderLeft: "4px solid #dc2626",
                    }}
                  >
                    <div>
                      <strong>İptal #{idx + 1} Detayları</strong>
                      {c.created_at && (
                        <span className="text-muted ms-2">
                          {formatDateTR(c.created_at)}
                        </span>
                      )}
                    </div>
                    <div className="mt-1">
                      <div className="mb-1">
                        <strong>İptal Miktarı:</strong> {c.cancelled_qty} |{" "}
                        <strong>İade Tutarı:</strong> {c.refund_amount}{" "}
                        {item.currency_symbol}
                      </div>
                      {c.reason && (
                        <div>
                          <strong>Sebep:</strong> {c.reason}
                        </div>
                      )}
                      {c.note && (
                        <div>
                          <strong>Not:</strong> {c.note}
                        </div>
                      )}
                      {!c.reason && !c.note && (
                        <div className="text-muted fst-italic">
                          İptal sebebi veya notu girilmemiş.
                        </div>
                      )}
                      {renderFiles(c.files)}
                    </div>
                  </div>
                ))}
              </div>
            </td>
          </tr>
        )}
      </React.Fragment>
    );

    const tableHead = (
      <thead className="table-light">
        <tr>
          <th>#</th>
          <th>{getText("IMAGE", "Görsel")}</th>
          <th>{getText("TABLE_BARCODE_LABEL", "Barkod")}</th>
          <th>{getText("TABLE_TITLE_LABEL", "Ürün")}</th>
          <th>{getText("TABLE_QUANTITY_LABEL", "Miktar")}</th>
          <th>{getText("UNIT_PRICE_LABEL", "Birim Fiyat")}</th>
          <th>{getText("TOTAL_LABEL", "Toplam")}</th>
          <th style={{ minWidth: "100px" }}>{getText("TABLE_STATUS_LABEL")}</th>
        </tr>
      </thead>
    );

    return (
      <>
        {/* TAM İPTAL DURUMU */}
        {isFullyCancelled ? (
          <div
            className="mt-4 rounded shadow-sm"
            style={{ border: "1px solid #fca5a5", overflow: "hidden" }}
          >
            <div
              className="px-3 py-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2"
              style={{
                background: "#fff5f5",
                borderBottom: "1px solid #fca5a5",
              }}
            >
              <h6
                className="mb-0 fw-bold d-flex align-items-center gap-2"
                style={{ color: "#dc2626" }}
              >
                <i className="ph ph-x-circle fs-5" />
                {getText("MENU_PRODUCTS", "Ürünler")} ({allItems.length})
              </h6>
              <span
                className="badge align-self-start align-self-sm-auto"
                style={{
                  background: "#fee2e2",
                  color: "#b91c1c",
                  padding: "6px 12px",
                  borderRadius: "99px",
                }}
              >
                Sipariş İptal Edildi
              </span>
            </div>
            <div className="p-3 bg-white">
              {allItems.map((item, idx) => (
                <div
                  key={`${item.id}-c-${idx}`}
                  className="card mb-3 border-0"
                  style={{
                    background: "#fff5f5",
                    boxShadow: "inset 0 0 0 1px #fca5a5",
                  }}
                >
                  <div className="card-body p-3">
                    <div className="row g-3 align-items-center">
                      <div className="col-auto">
                        <div
                          style={{
                            width: "75px",
                            height: "75px",
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            border: "1px solid #fca5a5",
                            padding: "4px",
                          }}
                        >
                          {item.main_image ? (
                            <img
                              src={buildImageUrl(
                                item.main_image_cdn,
                                item.main_image,
                              )}
                              alt={item.title}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "contain",
                                filter: "grayscale(100%)",
                              }}
                              onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = "none";
                              }}
                            />
                          ) : (
                            <span className="text-muted d-flex h-100 align-items-center justify-content-center small">
                              Yok
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="col">
                        <h6
                          className="fw-bold mb-1"
                          style={{ color: "#dc2626" }}
                        >
                          {item.title}
                        </h6>
                        <div
                          className="small mb-2"
                          style={{ color: "#b91c1c" }}
                        >
                          {item.barcode && (
                            <span className="me-3 d-inline-block">
                              {getText("TABLE_BARCODE_LABEL")}: {item.barcode}
                            </span>
                          )}
                          {item.sku && (
                            <span className="d-inline-block">
                              SKU: {item.sku}
                            </span>
                          )}
                        </div>
                        <div className="small" style={{ color: "#991b1b" }}>
                          <span
                            className="badge me-2"
                            style={{ background: "#fee2e2", color: "#dc2626" }}
                          >
                            {item.qty} Adet
                          </span>
                          <span>
                            Birim: {item.price} {item.currency_symbol}
                          </span>
                        </div>
                      </div>
                      <div
                        className="col-12 col-sm-auto text-sm-end mt-3 mt-sm-0 border-top border-sm-0 pt-3 pt-sm-0"
                        style={{ borderColor: "#fca5a5" }}
                      >
                        <div className="fw-bold text-danger fs-5">
                          {(item.qty * item.price).toFixed(2)}{" "}
                          {item.currency_symbol}
                        </div>
                        <span
                          className="badge mt-1"
                          style={{ background: "#dc2626", color: "#fff" }}
                        >
                          ✕ İptal
                        </span>
                      </div>
                    </div>

                    {item.cancellations && item.cancellations.length > 0 && (
                      <div className="mt-3">
                        {item.cancellations.map((c, cIdx) => (
                          <div
                            key={cIdx}
                            className="p-3 mb-2 small rounded bg-white"
                            style={{
                              borderLeft: "4px solid #dc2626",
                              border: "1px solid #fca5a5",
                            }}
                          >
                            <div className="fw-bold mb-1 text-danger">
                              İptal Detayları{" "}
                              <span
                                className="text-muted fw-normal ms-2"
                                style={{ fontSize: "11px" }}
                              >
                                {formatDateTR(c.created_at)}
                              </span>
                            </div>
                            <div className="text-dark">
                              <strong>İptal Miktarı:</strong> {c.cancelled_qty}{" "}
                              | <strong>İade Tutarı:</strong> {c.refund_amount}{" "}
                              {item.currency_symbol}
                            </div>
                            {c.reason && (
                              <div className="text-dark mt-1">
                                <strong>Sebep:</strong> {c.reason}
                              </div>
                            )}
                            {c.note && (
                              <div className="text-dark mt-1">
                                <strong>Not:</strong> {c.note}
                              </div>
                            )}
                            {!c.reason && !c.note && (
                              <div className="text-muted fst-italic mt-1">
                                İptal sebebi veya notu girilmemiş.
                              </div>
                            )}
                            {renderFiles(c.files)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <>
            {/* AKTİF ÜRÜNLER */}
            {activeItems.length > 0 && (
              <div
                className="mt-4 rounded shadow-sm"
                style={{ border: "1px solid #e2e8f0", overflow: "hidden" }}
              >
                <div
                  className="px-3 py-3 d-flex flex-column flex-sm-row align-items-sm-center gap-2"
                  style={{
                    background: "#f8fafc",
                    borderBottom: "1px solid #e2e8f0",
                  }}
                >
                  <h6 className="mb-0 fw-bold d-flex align-items-center gap-2">
                    <i className="ph ph-package fs-5 text-main" />
                    {isPartialCancel || returnRows.length > 0
                      ? "Aktif Ürünler"
                      : getText("MENU_PRODUCTS", "Ürünler")}{" "}
                    ({activeItems.length})
                  </h6>
                </div>
                <div className="p-3 bg-white">
                  {activeItems.map((item, idx) => (
                    <div
                      key={`${item.id}-a-${idx}`}
                      className="card mb-3 border-0"
                      style={{
                        background: "#f8fafc",
                        boxShadow: "inset 0 0 0 1px #e2e8f0",
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="row g-3 align-items-center">
                          {/* CHECKBOX */}
                          <div className="col-auto">
                            <input
                              type="checkbox"
                              checked={preparedItems[item.id] || false}
                              onChange={() => togglePrepared(item.id)}
                              style={{
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                              }}
                            />
                          </div>
                          <div className="col-auto">
                            <div
                              style={{
                                width: "75px",
                                height: "75px",
                                backgroundColor: "#fff",
                                borderRadius: "8px",
                                border: "1px solid #e2e8f0",
                                padding: "4px",
                              }}
                            >
                              {item.main_image ? (
                                <img
                                  src={buildImageUrl(
                                    item.main_image_cdn,
                                    item.main_image,
                                  )}
                                  alt={item.title}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "none";
                                  }}
                                />
                              ) : (
                                <span className="text-muted d-flex h-100 align-items-center justify-content-center small">
                                  Yok
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col">
                            <h6 className="fw-bold mb-1 text-dark">
                              {item.title}
                            </h6>
                            <div className="text-muted small mb-2">
                              {item.barcode && (
                                <span className="me-3 d-inline-block">
                                  {getText("TABLE_BARCODE_LABEL")}: {item.barcode}
                                </span>
                              )}
                              {item.sku && (
                                <span className="d-inline-block">
                                  {getText("TABLE_SKU_LABEL")}: {item.sku}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-auto ms-auto text-end d-flex flex-column align-items-end justify-content-center gap-1">
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#64748b",
                                fontWeight: 500,
                              }}
                            >
                              x{item.qty}
                            </span>
                            <span
                              style={{ fontSize: "13px", color: "#475569" }}
                            >
                              {item.price} {item.currency_symbol}
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: "#0f172a",
                              }}
                            >
                              {(item.qty * item.price).toFixed(2)}{" "}
                              {item.currency_symbol}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* İADE EDİLEN ÜRÜNLER */}
            {returnRows.length > 0 && (
              <div
                className="mt-4 rounded shadow-sm"
                style={{ border: "1.5px solid #fcd34d", overflow: "hidden" }}
              >
                <div
                  className="px-3 py-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2"
                  style={{
                    background: "#fffbeb",
                    borderBottom: "1px solid #fde68a",
                  }}
                >
                  <h6
                    className="mb-0 fw-bold d-flex align-items-center gap-2"
                    style={{ color: "#d97706" }}
                  >
                    <i className="ph ph-arrow-counter-clockwise fs-5" />
                    İade Edilen Ürünler ({returnRows.length})
                  </h6>
                  <span
                    className="badge align-self-start align-self-sm-auto"
                    style={{
                      background: "#fef3c7",
                      color: "#b45309",
                      padding: "6px 12px",
                      borderRadius: "99px",
                    }}
                  >
                    İade Talepleri
                  </span>
                </div>
                <div className="p-3 bg-white">
                  {returnRows.map((row, idx) => {
                    const { itemRef, retData } = row;
                    const retStatus = retData.status || "pending";
                    const isReturnStatusDisabled =
                      statusUpdating ||
                      retStatus === "completed" ||
                      retStatus === "rejected";

                    return (
                      <div
                        key={`ret-${retData.id}`}
                        className="card mb-3 border-0"
                        style={{
                          background: "#fffbeb",
                          boxShadow: "inset 0 0 0 1px #fde68a",
                        }}
                      >
                        <div className="card-body p-3">
                          <div className="row g-3 align-items-sm-center">
                            <div className="col-auto">
                              <div
                                style={{
                                  width: "75px",
                                  height: "75px",
                                  backgroundColor: "#fff",
                                  borderRadius: "8px",
                                  border: "1px solid #fde68a",
                                  padding: "4px",
                                }}
                              >
                                {itemRef.main_image ? (
                                  <img
                                    src={buildImageUrl(
                                      itemRef.main_image_cdn,
                                      itemRef.main_image,
                                    )}
                                    alt={itemRef.title}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      objectFit: "contain",
                                    }}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "none";
                                    }}
                                  />
                                ) : (
                                  <span className="text-muted d-flex h-100 align-items-center justify-content-center small">
                                    Yok
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="col">
                              <h6
                                className="fw-bold mb-1"
                                style={{ color: "#d97706" }}
                              >
                                {itemRef.title}
                              </h6>
                              <div
                                className="small mb-2"
                                style={{ color: "#b45309" }}
                              >
                                {itemRef.barcode && (
                                  <span className="me-3 d-inline-block">
                                    {getText("TABLE_BARCODE_LABEL")}: {itemRef.barcode}
                                  </span>
                                )}
                                {itemRef.sku && (
                                  <span className="d-inline-block">
                                    SKU: {itemRef.sku}
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="col-auto ms-auto text-end d-flex flex-column align-items-end justify-content-center gap-1">
                              <span
                                style={{
                                  fontSize: "13px",
                                  color: "#fbbf24",
                                  fontWeight: 500,
                                }}
                              >
                                x{retData.returned_qty}
                              </span>
                              <span
                                style={{ fontSize: "13px", color: "#d97706" }}
                              >
                                {itemRef.price} {itemRef.currency_symbol}
                              </span>
                              <span
                                style={{
                                  fontSize: "16px",
                                  fontWeight: 700,
                                  color: "#b45309",
                                }}
                              >
                                {(itemRef.price * retData.returned_qty).toFixed(
                                  2,
                                )}{" "}
                                {itemRef.currency_symbol}
                              </span>
                              <select
                                className={`form-select form-select-sm fw-bold shadow-sm mt-1 ${
                                  retStatus === "completed"
                                    ? "text-success border-success"
                                    : retStatus === "rejected"
                                      ? "text-danger border-danger"
                                      : "text-warning border-warning"
                                }`}
                                style={{ maxWidth: "140px", fontSize: "12px" }}
                                value={retStatus}
                                onChange={(e) =>
                                  handleReturnStatusChange(
                                    itemRef.id,
                                    retData.id,
                                    e.target.value,
                                  )
                                }
                                disabled={isReturnStatusDisabled}
                              >
                                <option value="pending">İade Alındı</option>
                                <option value="approved">Onaylandı</option>
                                <option value="completed">Tamamlandı</option>
                                <option value="rejected">Reddedildi</option>
                              </select>
                            </div>
                          </div>

                          <div
                            className="mt-3 p-3 small rounded bg-white"
                            style={{
                              borderLeft: "4px solid #d97706",
                              border: "1px solid #fde68a",
                            }}
                          >
                            <div
                              className="fw-bold mb-1"
                              style={{ color: "#d97706" }}
                            >
                              İade Talebi Detayları{" "}
                              <span
                                className="text-muted fw-normal ms-2"
                                style={{ fontSize: "11px" }}
                              >
                                {formatDateTR(retData.created_at)}
                              </span>
                            </div>
                            <div className="text-dark">
                              <strong>İade Miktarı:</strong>{" "}
                              {retData.returned_qty} |{" "}
                              <strong>İade Tutarı:</strong>{" "}
                              {(itemRef.price * retData.returned_qty).toFixed(
                                2,
                              )}{" "}
                              {itemRef.currency_symbol}
                            </div>
                            {retData.reason && (
                              <div className="text-dark mt-1">
                                <strong>Sebep:</strong> {retData.reason}
                              </div>
                            )}
                            {retData.note && (
                              <div className="text-dark mt-1">
                                <strong>Müşteri Notu:</strong> {retData.note}
                              </div>
                            )}
                            {!retData.reason && !retData.note && (
                              <div className="text-muted fst-italic mt-1">
                                İade sebebi veya notu girilmemiş.
                              </div>
                            )}
                            {renderFiles(retData.files)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* KISMİ İPTAL EDİLEN ÜRÜNLER */}
            {isPartialCancel && (
              <div
                className="mt-4 rounded shadow-sm"
                style={{ border: "1.5px solid #fca5a5", overflow: "hidden" }}
              >
                <div
                  className="px-3 py-3 d-flex flex-column flex-sm-row align-items-sm-center justify-content-between gap-2"
                  style={{
                    background: "#fff5f5",
                    borderBottom: "1px solid #fca5a5",
                  }}
                >
                  <h6
                    className="mb-0 fw-bold d-flex align-items-center gap-2"
                    style={{ color: "#dc2626" }}
                  >
                    <i className="ph ph-x-circle fs-5" />
                    İptal Edilen Ürünler ({cancelledItems.length})
                  </h6>
                  <span
                    className="badge align-self-start align-self-sm-auto"
                    style={{
                      background: "#fee2e2",
                      color: "#b91c1c",
                      padding: "6px 12px",
                      borderRadius: "99px",
                    }}
                  >
                    Kısmi İptal
                  </span>
                </div>
                <div className="p-3 bg-white">
                  {cancelledItems.map((item, idx) => (
                    <div
                      key={`${item.id}-pc-${idx}`}
                      className="card mb-3 border-0"
                      style={{
                        background: "#fff5f5",
                        boxShadow: "inset 0 0 0 1px #fca5a5",
                      }}
                    >
                      <div className="card-body p-3">
                        <div className="row g-3 align-items-center">
                          <div className="col-auto">
                            <div
                              style={{
                                width: "75px",
                                height: "75px",
                                backgroundColor: "#fff",
                                borderRadius: "8px",
                                border: "1px solid #fca5a5",
                                padding: "4px",
                              }}
                            >
                              {item.main_image ? (
                                <img
                                  src={buildImageUrl(
                                    item.main_image_cdn,
                                    item.main_image,
                                  )}
                                  alt={item.title}
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    objectFit: "contain",
                                    filter: "grayscale(100%)",
                                  }}
                                  onError={(e) => {
                                    e.target.onerror = null;
                                    e.target.src = "none";
                                  }}
                                />
                              ) : (
                                <span className="text-muted d-flex h-100 align-items-center justify-content-center small">
                                  Yok
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col">
                            <h6
                              className="fw-bold mb-1"
                              style={{ color: "#dc2626" }}
                            >
                              {item.title}
                            </h6>
                            <div
                              className="small mb-2"
                              style={{ color: "#b91c1c" }}
                            >
                              {item.barcode && (
                                <span className="me-3 d-inline-block">
                                  {getText("TABLE_BARCODE_LABEL")}: {item.barcode}
                                </span>
                              )}
                              {item.sku && (
                                <span className="d-inline-block">
                                  {getText("TABLE_SKU_LABEL")}: {item.sku}
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="col-auto ms-auto text-end d-flex flex-column align-items-end justify-content-center gap-1">
                            <span
                              style={{
                                fontSize: "13px",
                                color: "#f87171",
                                fontWeight: 500,
                              }}
                            >
                              x{item.qty}
                            </span>
                            <span
                              style={{ fontSize: "13px", color: "#ef4444" }}
                            >
                              {item.price} {item.currency_symbol}
                            </span>
                            <span
                              style={{
                                fontSize: "16px",
                                fontWeight: 700,
                                color: "#dc2626",
                              }}
                            >
                              {(item.qty * item.price).toFixed(2)}{" "}
                              {item.currency_symbol}
                            </span>
                            <span
                              className="badge mt-1"
                              style={{ background: "#dc2626", color: "#fff" }}
                            >
                              ✕ İptal
                            </span>
                          </div>
                        </div>

                        {item.cancellations &&
                          item.cancellations.length > 0 && (
                            <div className="mt-3">
                              {item.cancellations.map((c, cIdx) => (
                                <div
                                  key={cIdx}
                                  className="p-3 mb-2 small rounded bg-white"
                                  style={{
                                    borderLeft: "4px solid #dc2626",
                                    border: "1px solid #fca5a5",
                                  }}
                                >
                                  <div className="fw-bold mb-1 text-danger">
                                    İptal Detayları{" "}
                                    <span
                                      className="text-muted fw-normal ms-2"
                                      style={{ fontSize: "11px" }}
                                    >
                                      {formatDateTR(c.created_at)}
                                    </span>
                                  </div>
                                  <div className="text-dark">
                                    <strong>İptal Miktarı:</strong>{" "}
                                    {c.cancelled_qty} |{" "}
                                    <strong>İade Tutarı:</strong>{" "}
                                    {c.refund_amount} {item.currency_symbol}
                                  </div>
                                  {c.reason && (
                                    <div className="text-dark mt-1">
                                      <strong>Sebep:</strong> {c.reason}
                                    </div>
                                  )}
                                  {c.note && (
                                    <div className="text-dark mt-1">
                                      <strong>Not:</strong> {c.note}
                                    </div>
                                  )}
                                  {!c.reason && !c.note && (
                                    <div className="text-muted fst-italic mt-1">
                                      İptal sebebi veya notu girilmemiş.
                                    </div>
                                  )}
                                  {renderFiles(c.files)}
                                </div>
                              ))}
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        {/* TOPLAMLAR BÖLÜMÜ */}
        <div
          className="text-end mt-4 p-4 rounded shadow-sm"
          style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}
        >
          <h4 className="fw-bold mb-1">
            {getText("TOTAL_AMOUNT_LABEL", "Toplam")}:{" "}
            <span className="text-success">
              {activeTotal.toFixed(2)} {order.currency_symbol}
            </span>
          </h4>
          {(cancelledItems.length > 0 || returnRows.length > 0) && (
            <h5 className="fw-bold mt-2 mb-0">
              Toplam İade:{" "}
              <span className="text-danger">
                {order.items
                  .reduce((sum, i) => sum + (i.refund_amount || 0), 0)
                  .toFixed(2)}{" "}
                {order.currency_symbol}
              </span>
            </h5>
          )}
        </div>
      </>
    );
  };

  return (
    <div className="container mt-4">
            {paymentNoteModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(15,23,42,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}
          onClick={(e) =>
            e.target === e.currentTarget && setPaymentNoteModal(false)
          }
        >
          <div
            style={{
              background: "#fff",
              borderRadius: "20px",
              padding: "32px",
              width: "100%",
              maxWidth: "460px",
              boxShadow: "0 24px 60px rgba(15,23,42,0.18)",
              border: "1.5px solid #e2e8f0",
              margin: "0 16px",
            }}
          >
            {/* Başlık */}
            <div className="d-flex align-items-center gap-3 mb-4">
              <span
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,#6366f1,#0ea5e9)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <i
                  className="ph ph-currency-circle-dollar"
                  style={{ color: "#fff", fontSize: 22 }}
                />
              </span>
              <div>
                <div
                  className="fw-bold"
                  style={{ fontSize: 17, color: "#0f172a" }}
                >
                  {getText("PAYMENT_RECEIVED_TITLE")}
                </div>
                <div style={{ fontSize: 13, color: "#64748b" }}>
                  {getText("PAYMENT_RECEIVED_DESC")}
                </div>
              </div>
            </div>

            {/* Ödeme yöntemi */}
            <div className="mb-3">
              <label
                className="form-label fw-semibold"
                style={{ fontSize: 13, color: "#374151" }}
              >
                {getText("PAYMENT_METHOD_LABEL")}
              </label>
              <div className="d-flex gap-2 flex-wrap">
                {[
                  { val: getText("PAYMENT_CASH"), icon: "ph-money" },
                  { val: getText("PAYMENT_CARD"), icon: "ph-credit-card" },
                  { val: getText("PAYMENT_TRANSFER"), icon: "ph-bank" },
                  { val: getText("PAYMENT_OTHER"), icon: "ph-dots-three" },
                ].map(({ val, icon }) => (
                  <button
                    key={val}
                    type="button"
                    onClick={() =>
                      setPaymentNote((prev) => {
                        // başa ödeme yöntemini ekle / değiştir
                        const lines = prev
                          .split("\n")
                          .filter(
                            (l) =>
                              ![
                                getText("PAYMENT_CASH"),
                                getText("PAYMENT_CARD"),
                                getText("PAYMENT_TRANSFER"),
                                getText("PAYMENT_OTHER"),
                              ].some((p) => l.startsWith(p)),
                          );
                        return [val, ...lines].join("\n").trim();
                      })
                    }
                    style={{
                      borderRadius: "99px",
                      border: paymentNote.startsWith(val)
                        ? "1.5px solid #6366f1"
                        : "1.5px solid #e2e8f0",
                      background: paymentNote.startsWith(val)
                        ? "#eef2ff"
                        : "#f8fafc",
                      color: paymentNote.startsWith(val)
                        ? "#6366f1"
                        : "#475569",
                      fontSize: 12,
                      fontWeight: 600,
                      padding: "6px 14px",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 5,
                    }}
                  >
                    <i className={`ph ${icon}`} style={{ fontSize: 14 }} />
                    {val}
                  </button>
                ))}
              </div>
            </div>

            {/* Not textarea */}
            <div className="mb-4">
              <label
                className="form-label fw-semibold"
                style={{ fontSize: 13, color: "#374151" }}
              >
                {getText("PAYMENT_NOTE_LABEL")}{" "}
                <span style={{ color: "#94a3b8", fontWeight: 400 }}>
                  ({getText("OPTIONAL_LABEL")})
                </span>
              </label>
              <textarea
                className="form-control"
                rows={4}
                style={{
                  borderRadius: "10px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: 13,
                  resize: "vertical",
                  boxShadow: "none",
                  background: "#f8fafc",
                }}
                placeholder={getText("PAYMENT_NOTE_PLACEHOLDER")}
                value={paymentNote}
                onChange={(e) => setPaymentNote(e.target.value)}
              />
            </div>

            {/* Butonlar */}
            <div className="d-flex gap-2">
              <button
                className="btn flex-grow-1 fw-semibold"
                style={{
                  borderRadius: "10px",
                  border: "1.5px solid #e2e8f0",
                  background: "#f8fafc",
                  color: "#64748b",
                  fontSize: 14,
                }}
                onClick={() => {
                  setPaymentNoteModal(false);
                  setPaymentNote("");
                  setPendingStatus(null);
                }}
              >
                {getText("CANCEL_BUTTON")}
              </button>
              <button
                className="btn flex-grow-1 fw-semibold"
                style={{
                  borderRadius: "10px",
                  background: "linear-gradient(135deg,#6366f1,#0ea5e9)",
                  color: "#fff",
                  border: "none",
                  fontSize: 14,
                }}
                onClick={handlePaymentNoteConfirm}
                disabled={statusUpdating}
              >
                {statusUpdating ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {getText("SAVING_LABEL")}
                  </>
                ) : (
                  <>
                    <i className="ph ph-check me-1" />
                    {getText("CONFIRM_SAVE_LABEL")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="card shadow-sm p-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h3>{getText("ORDER_DETAIL_TITLE", "Sipariş Detayları")}</h3>
          <Link to="/order/table" className="btn btn-outline-secondary">
            {getText("BACK", "Geri")}
          </Link>
        </div>

        {/* TAM İPTAL UYARISI */}
        {order.status === "cancelled" && (
          <div className="alert alert-danger mb-3" role="alert">
            <strong>⚠️ Bu sipariş iptal edilmiştir!</strong>
            {order.cancellations?.full_order_cancellation && (
              <div className="mt-2 small">
                <div>
                  <strong>İptal Tarihi:</strong>{" "}
                  {formatDateTR(
                    order.cancellations.full_order_cancellation.created_at,
                  )}
                </div>
                {order.cancellations.full_order_cancellation.reason && (
                  <div>
                    <strong>Sebep:</strong>{" "}
                    {order.cancellations.full_order_cancellation.reason}
                  </div>
                )}
                {order.cancellations.full_order_cancellation.note && (
                  <div>
                    <strong>Not:</strong>{" "}
                    {order.cancellations.full_order_cancellation.note}
                  </div>
                )}
                <div>
                  <strong>İade Tutarı:</strong>{" "}
                  <span className="text-danger">
                    {order.cancellations.full_order_cancellation.refund_amount}{" "}
                    {order.currency_symbol}
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* KISMİ İPTAL UYARISI */}
        {order.cancellations?.has_any_cancellation &&
          order.status !== "cancelled" && (
            <div className="alert alert-warning mb-3" role="alert">
              <strong>⚠️ Bu siparişte kısmi iptaller var!</strong>
              <div className="mt-2 small">
                Toplam {order.cancellations.total_cancellations} iptal işlemi
                bulunmaktadır.
              </div>
            </div>
          )}

        <div className="row mt-3 mb-4 g-3">
          {/* SOL: Sipariş Durumu */}
          <div className="col-md-6">
            <div
              style={{
                borderRadius: "16px",
                border: "1.5px solid #e2e8f0",
                background: "#fff",
                padding: "20px",
                height: "100%",
                boxShadow: "0 2px 12px 0 rgba(15,23,42,0.06)",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg,#6366f1,#0ea5e9)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <i
                    className="ph ph-tag-simple"
                    style={{ color: "#fff", fontSize: 17 }}
                  />
                </span>
                <span
                  className="fw-bold"
                  style={{ fontSize: 15, color: "#0f172a" }}
                >
                  {getText("ORDER_STATUS_LABEL")}
                </span>
              </div>

              <select
                className="form-select form-select-lg fw-semibold"
                style={{
                  borderRadius: "10px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: 14,
                  background: "#f8fafc",
                  color: "#0f172a",
                  boxShadow: "none",
                }}
                value={currentMainStatus}
                onChange={(e) => handleStatusChange(e.target.value)}
                disabled={isMainStatusDisabled}
              >
                {STATUS_OPTIONS.map((opt) => (
                  <option
                    key={opt.value}
                    value={opt.value}
                    disabled={opt.value === "shipped" && !trackingNumber}
                  >
                    {opt.label}
                  </option>
                ))}
              </select>

              {statusUpdating && (
                <div
                  className="d-flex align-items-center gap-1 mt-2"
                  style={{ fontSize: 12, color: "#64748b" }}
                >
                  <span
                    className="spinner-border spinner-border-sm"
                    style={{ width: 12, height: 12 }}
                  />
                  Güncelleniyor...
                </div>
              )}
            </div>
          </div>

          {/* SAĞ: Tracking */}
          <div className="col-md-6">
            <div
              style={{
                borderRadius: "16px",
                border: `1.5px solid ${isTrackingEditable ? "#a3e635" : "#e2e8f0"}`,
                background: isTrackingEditable ? "#f7fee7" : "#f8fafc",
                padding: "20px",
                height: "100%",
                boxShadow: "0 2px 12px 0 rgba(15,23,42,0.06)",
                transition: "border-color 0.2s, background 0.2s",
              }}
            >
              <div className="d-flex align-items-center gap-2 mb-3">
                <span
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: "50%",
                    background: isTrackingEditable
                      ? "linear-gradient(135deg,#65a30d,#a3e635)"
                      : "linear-gradient(135deg,#94a3b8,#cbd5e1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "background 0.2s",
                  }}
                >
                  <i
                    className="ph ph-truck"
                    style={{ color: "#fff", fontSize: 17 }}
                  />
                </span>
                <span
                  className="fw-bold"
                  style={{ fontSize: 15, color: "#0f172a" }}
                >
                  {getText("CARGO_TRACKING_LABEL")}
                </span>
                {!isTrackingEditable && (
                  <span
                    style={{
                      marginLeft: "auto",
                      fontSize: 11,
                      color: "#94a3b8",
                      background: "#e2e8f0",
                      borderRadius: 99,
                      padding: "2px 10px",
                      fontWeight: 600,
                    }}
                  >
                    <i
                      className="ph ph-lock-simple"
                      style={{ fontSize: 11, marginRight: 3 }}
                    />
                    {getText("LOCKED_STATUS_LABEL")}
                  </span>
                )}
              </div>

              <input
                type="text"
                className="form-control mb-2"
                style={{
                  borderRadius: "10px",
                  border: "1.5px solid #e2e8f0",
                  fontSize: 14,
                  background: isTrackingEditable ? "#fff" : "#f1f5f9",
                  color: isTrackingEditable ? "#0f172a" : "#94a3b8",
                  boxShadow: "none",
                }}
                placeholder={
                  isTrackingEditable
                    ? getText("TRACKING_INPUT_PLACEHOLDER")
                    : getText("TRACKING_DISABLED_PLACEHOLDER")
                }
                value={trackingNumber}
                onChange={(e) =>
                  isTrackingEditable && setTrackingNumber(e.target.value)
                }
                disabled={!isTrackingEditable}
                readOnly={!isTrackingEditable}
              />

              <button
                className="btn w-100 fw-semibold"
                style={{
                  borderRadius: "10px",
                  background:
                    isTrackingEditable && trackingNumber
                      ? "linear-gradient(135deg,#65a30d,#84cc16)"
                      : "#e2e8f0",
                  color:
                    isTrackingEditable && trackingNumber ? "#fff" : "#94a3b8",
                  border: "none",
                  fontSize: 14,
                  padding: "9px 0",
                  transition: "all 0.2s",
                  cursor:
                    isTrackingEditable && trackingNumber
                      ? "pointer"
                      : "not-allowed",
                }}
                onClick={handleTrackingSave}
                disabled={
                  trackingLoading || !trackingNumber || !isTrackingEditable
                }
              >
                {trackingLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    {getText("SAVE_BTN")}
                  </>
                ) : (
                  <>
                    <i className="ph ph-floppy-disk me-1" />
                    {getText("SAVE_AND_SHIP_LABEL")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        <hr />

        {/* SİPARİŞ & MÜŞTERİ BİLGİLERİ */}
        <div className="row mb-4">
          <div className="col-md-6">
            <h5 className="fw-bold mb-3">
              {getText("ORDER_INFORMATION_TITLE", "Sipariş Bilgileri")}
            </h5>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TABLE_ORDER_NO_LABEL", "Sipariş No")}:
              </strong>
              <span>{order.merchant_oid}</span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("LABEL_CREATED_AT", "Oluşturulma")}:
              </strong>
              <span>{formatDateTR(order.created_at)}</span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TABLE_PAID_LABEL", "Ödeme Yöntemi")}:
              </strong>
              <span>
                {order.payment_method === "cash"
                  ? getText("PAYMENT_CASH", "Nakit")
                  : order.payment_method === "card"
                    ? getText("PAYMENT_CARD", "Kredi Kartı")
                    : order.payment_method === "havale"
                      ? getText("PAYMENT_TRANSFER", "Havale / EFT")
                      : "—"}
              </span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("DELIVERY_TYPE")}:
              </strong>

              {shippingData?.type === "store" ? (
                <span className="d-flex align-items-center">
                  <i
                    className="ph ph-storefront"
                    style={{ color: "#6366f1" }}
                  />
                  <strong>{getText("ORDER_STORE_NAME")}: </strong>{" "}
                  {shippingData.warehouse_name}
                </span>
              ) : shippingData?.type === "cargo" ? (
                <span className="d-flex align-items-center">
                  <i className="ph ph-truck" style={{ color: "#0ea5e9" }} />
                  <strong>{getText("ORDER_CARGO_NAME")}: </strong>{" "}
                  {[
                    shippingData.address_line,
                    shippingData.district,
                    shippingData.city,
                    shippingData.region,
                    shippingData.country,
                    shippingData.postal_code,
                  ]
                    .filter(Boolean)
                    .join(", ")}
                </span>
              ) : (
                "—"
              )}
            </div>

            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("SALES_PERSONNEL_LABEL", "Satış Personeli")}:
              </strong>
              <span>{order.personnel_fullname || "—"}</span>
            </div>
            {order.note && (
              <div className="mb-2 d-flex">
                <strong style={{ width: "180px" }}>
                  {getText("ORDER_PAID_NOTE_LABEL")}:
                </strong>
                <span style={{ whiteSpace: "pre-line" }}>{order.note}</span>
              </div>
            )}
          </div>

          <div className="col-md-6">
            <h5 className="fw-bold mb-3">
              {getText("CUSTOMER_INFORMATION_TITLE", "Müşteri Bilgileri")}
            </h5>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TABLE_NAME_LABEL", "Ad Soyad")}:
              </strong>
              <span>{order.customer_fullname}</span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TABLE_EMAIL_LABEL", "E-Posta")}:
              </strong>
              <span>{order.customer_email || "—"}</span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TABLE_PHONE_LABEL", "Telefon")}:
              </strong>
              <span>{formatPhone(order.customer_phone)}</span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TABLE_COUNTRY_LABEL", "Ülke")}:
              </strong>
              <span>{order.customer_country}</span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TABLE_ADDRESS_LABEL", "Adres")}:
              </strong>
              <span>
                {shippingData?.type === "cargo"
                  ? [
                      shippingData.address_line,
                      shippingData.district,
                      shippingData.city,
                      shippingData.region,
                      shippingData.country,
                      shippingData.postal_code,
                    ]
                      .filter(Boolean)
                      .join(", ")
                  : "—"}
              </span>
            </div>
            <div className="mb-2 d-flex">
              <strong style={{ width: "180px" }}>
                {getText("TAX_NUMBER_LABEL", "Vergi No")}:
              </strong>
              <span>{order.customer_tax_number || "—"}</span>
            </div>
          </div>
        </div>

        {/* ÜRÜN TABLOLARI */}
        {renderProductTables()}
      </div>
    </div>
  );
};

export default OrdersView;
